import React from "react";
//#region types
import { InvoiceReminderGroupProps } from "models/propTypes/invoicePropTypes";
import { TranslationKeys } from "translations";
import {
  ChoiceProps,
  NumberInputProps,
  SelectInputProps,
  TextInputProps,
  ToggleProps,
} from "models/propTypes/wintFormPropsTypes";
import { FeatureConstants, IconDefinitionConstants } from "utils/constants";
import { WintSuperkollAPIModel } from "wint-api-client/dist/types";
//#endregion
//#region components
import {
  CurrencyInput,
  HorizontalFormField,
  NumberInput,
  TextAreaInput,
  Choice,
  Toggle,
  SelectInput,
  GroupSection,
} from "components/common/wintForms";
import SendMailFormFields from "./sendMailFormFields";
import { useFeatureService } from "serviceHooks";
//#endregion
import { EnumHelper, LocalizationHelperInstance } from "utils/helpers";

function ReminderGroup({ connect, values, invoice }: InvoiceReminderGroupProps) {
  const featureService = useFeatureService();
  const [isAutomaticRemindersFeatureEnabled, setIsAutomaticRemindersFeatureEnabled] = React.useState(false);

  React.useEffect(() => {
    featureService
      .hasFeature(FeatureConstants.AutomaticReminders)
      .then((res) => setIsAutomaticRemindersFeatureEnabled(res));
  }, []);

  return (
    <GroupSection
      header={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Invoice.Reminders)}
      icon={IconDefinitionConstants.WBell}
    >
      <HorizontalFormField<NumberInputProps>
        Component={NumberInput}
        {...connect("otherSettings.reminderCost")}
        label={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Invoice.ReminderCostSetting)}
        descriptionText={LocalizationHelperInstance.getFormattedMessage(
          TranslationKeys.Invoice.ReminderCostSettingDescription
        )}
        optional
      />
      <HorizontalFormField<ChoiceProps>
        Component={Choice}
        {...connect("otherSettings.penaltyInterestType")}
        label={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Invoice.PenaltyInterestType)}
        descriptionText={LocalizationHelperInstance.getFormattedMessage(
          TranslationKeys.Invoice.PenaltyInterestTypeDescription
        )}
        items={EnumHelper.getNumericItemValues(WintSuperkollAPIModel.InvoicePenaltyInterestType).map((state) => ({
          id: state,
          value: LocalizationHelperInstance.getFormattedMessage(`Invoice.PenaltyInterestType_${state}`),
        }))}
      />
      {values.otherSettings.penaltyInterestType === WintSuperkollAPIModel.InvoicePenaltyInterestType.Prearranged && (
        <HorizontalFormField<NumberInputProps>
          Component={NumberInput}
          {...connect("otherSettings.penaltyInterestPercentage")}
          label={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Invoice.InterestRate)}
          controlLabel={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Invoice.PenaltyInterest)}
          decimalPoints={2}
          suffix="%"
          maxNumber={100}
        />
      )}
      {isAutomaticRemindersFeatureEnabled ? (
        <HorizontalFormField<ToggleProps>
          Component={Toggle}
          {...connect("otherSettings.isAutomaticRemindersEnabled")}
          label={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Invoice.AutomaticReminder)}
        />
      ) : (
        <></>
      )}
      {values.otherSettings.isAutomaticRemindersEnabled ? (
        <>
          <HorizontalFormField<NumberInputProps>
            Component={CurrencyInput}
            {...connect("otherSettings.automaticReminderAmountLimit")}
            label={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Invoice.AutomaticReminderAmountLimit)}
            controlLabel={LocalizationHelperInstance.getFormattedMessage(
              TranslationKeys.Invoice.AutomaticReminderAmountLimitControlLabel
            )}
            descriptionText={LocalizationHelperInstance.getFormattedMessage(
              TranslationKeys.Invoice.AutomaticReminderAmountLimitDescription
            )}
            optional
          />
          <HorizontalFormField<NumberInputProps>
            Component={NumberInput}
            {...connect("otherSettings.automaticReminderDelay")}
            label={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Invoice.AutomaticReminderDelay)}
            controlLabel={LocalizationHelperInstance.getFormattedMessage(
              TranslationKeys.Invoice.AutomaticReminderDelayControlLabel
            )}
            descriptionText={LocalizationHelperInstance.getFormattedMessage(
              TranslationKeys.Invoice.AutomaticReminderDelayDescription
            )}
            optional
          />
          <HorizontalFormField<NumberInputProps>
            Component={NumberInput}
            {...connect("otherSettings.automaticReminderPeriod")}
            label={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Invoice.AutomaticReminderPeriod)}
            controlLabel={LocalizationHelperInstance.getFormattedMessage(
              TranslationKeys.Invoice.AutomaticReminderPeriodControlLabel
            )}
            descriptionText={LocalizationHelperInstance.getFormattedMessage(
              TranslationKeys.Invoice.AutomaticReminderPeriodDescription
            )}
          />
          <HorizontalFormField<NumberInputProps>
            Component={NumberInput}
            {...connect("otherSettings.automaticReminderCountLimit")}
            label={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Invoice.AutomaticReminderCountLimit)}
            controlLabel={LocalizationHelperInstance.getFormattedMessage(
              TranslationKeys.Invoice.AutomaticReminderCountLimitControlLabel
            )}
            descriptionText={LocalizationHelperInstance.getFormattedMessage(
              TranslationKeys.Invoice.AutomaticReminderCountLimitDescription
            )}
            optional
          />
          <HorizontalFormField<TextInputProps>
            Component={TextAreaInput}
            {...connect("otherSettings.automaticReminderNotes")}
            label={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Invoice.AutomaticReminderNotes)}
            descriptionText={LocalizationHelperInstance.getFormattedMessage(
              TranslationKeys.Invoice.AutomaticReminderNotesDescription
            )}
            optional
          />
          <HorizontalFormField<SelectInputProps>
            Component={SelectInput}
            {...connect("otherSettings.automaticReminderSendType")}
            label={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Invoice.AutomaticReminderSendType)}
            descriptionText={LocalizationHelperInstance.getFormattedMessage(
              TranslationKeys.Invoice.AutomaticReminderSendTypeDescription
            )}
            items={EnumHelper.getNumericItemValues(WintSuperkollAPIModel.InvoiceSendType).map((key) => ({
              value: key,
              label: LocalizationHelperInstance.getFormattedMessage(`Invoice.AutomaticReminderSendType_${key}`),
            }))}
          />
          {values.otherSettings.automaticReminderSendType === WintSuperkollAPIModel.InvoiceSendMethod.Email ? (
            <SendMailFormFields
              connect={connect}
              invoiceId={invoice?.id}
              mailOptions={values.mailOptions}
              isNotSendForm
              parentFieldName="otherSettings.automaticReminderSendMail"
            />
          ) : (
            <></>
          )}
        </>
      ) : (
        <></>
      )}
    </GroupSection>
  );
}

export default ReminderGroup;
